import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getPaymentsReport } from "../../services/Admin/OrdersApi";

const STATUS_COLORS = {
  completed: { bg: "#e8f5e9", color: "#2e7d32" },
  pending:   { bg: "#fff8e1", color: "#f57f17" },
  failed:    { bg: "#ffebee", color: "#c62828" },
};

const METHOD_LABELS = {
  easykash_card:   "EasyKash Card",
  easykash_wallet: "EasyKash Wallet",
  paymob:          "Paymob",
  cash:            "Cash",
};

const Dashboard = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));

  const [payments, setPayments] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [search, setSearch]     = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    getPaymentsReport()
      .then((res) => setPayments(res.data || []))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    navigate("/login", { replace: true });
  };

  const filtered = payments.filter((p) => {
    const matchStatus = statusFilter === "all" || p.status === statusFilter;
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      p.order?.user?.name?.toLowerCase().includes(q) ||
      p.order?.user?.email?.toLowerCase().includes(q) ||
      p.order?.order_number?.toLowerCase().includes(q) ||
      String(p.merchant_order_id).includes(q);
    return matchStatus && matchSearch;
  });

  const totalAmount    = filtered.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
  const completedCount = filtered.filter((p) => p.status === "completed").length;
  const pendingCount   = filtered.filter((p) => p.status === "pending").length;

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Payments Dashboard</h1>
          <p style={styles.subtitle}>Welcome, <strong>{user?.name}</strong></p>
        </div>
        <button onClick={handleLogout} style={styles.logout}>Logout</button>
      </div>

      {/* Summary Cards */}
      <div style={styles.cards}>
        <div style={styles.card}>
          <p style={styles.cardLabel}>Total Payments</p>
          <p style={styles.cardValue}>{filtered.length}</p>
        </div>
        <div style={{ ...styles.card, borderTop: "4px solid #2e7d32" }}>
          <p style={styles.cardLabel}>Completed</p>
          <p style={{ ...styles.cardValue, color: "#2e7d32" }}>{completedCount}</p>
        </div>
        <div style={{ ...styles.card, borderTop: "4px solid #f57f17" }}>
          <p style={styles.cardLabel}>Pending</p>
          <p style={{ ...styles.cardValue, color: "#f57f17" }}>{pendingCount}</p>
        </div>
        <div style={{ ...styles.card, borderTop: "4px solid #1565c0" }}>
          <p style={styles.cardLabel}>Total Amount</p>
          <p style={{ ...styles.cardValue, color: "#1565c0" }}>{totalAmount.toFixed(2)} EGP</p>
        </div>
      </div>

      {/* Filters */}
      <div style={styles.filters}>
        <input
          style={styles.search}
          placeholder="Search by name, email, or order number..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          style={styles.select}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All Statuses</option>
          <option value="completed">Completed</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <p style={styles.msg}>Loading payments...</p>
      ) : error ? (
        <p style={{ ...styles.msg, color: "#c62828" }}>Error: {error}</p>
      ) : filtered.length === 0 ? (
        <p style={styles.msg}>No payments found.</p>
      ) : (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.thead}>
                <th style={styles.th}>#</th>
                <th style={styles.th}>Order #</th>
                <th style={styles.th}>Client</th>
                <th style={styles.th}>Email</th>
                <th style={styles.th}>Method</th>
                <th style={styles.th}>Amount</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Transaction ID</th>
                <th style={styles.th}>Date</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p, i) => {
                const sc = STATUS_COLORS[p.status] || { bg: "#f5f5f5", color: "#333" };
                return (
                  <tr key={p.id} style={i % 2 === 0 ? styles.trEven : styles.trOdd}>
                    <td style={styles.td}>{p.id}</td>
                    <td style={styles.td}>{p.order?.order_number || "-"}</td>
                    <td style={styles.td}>{p.order?.user?.name || "-"}</td>
                    <td style={styles.td}>{p.order?.user?.email || "-"}</td>
                    <td style={styles.td}>{METHOD_LABELS[p.payment_method] || p.payment_method}</td>
                    <td style={{ ...styles.td, fontWeight: 600 }}>{parseFloat(p.amount).toFixed(2)} EGP</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, background: sc.bg, color: sc.color }}>
                        {p.status}
                      </span>
                    </td>
                    <td style={{ ...styles.td, fontSize: "12px", color: "#555" }}>
                      {p.transaction_id || <span style={{ color: "#bbb" }}>—</span>}
                    </td>
                    <td style={{ ...styles.td, fontSize: "12px" }}>
                      {new Date(p.created_at).toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Dashboard;

const styles = {
  page: {
    padding: "24px",
    fontFamily: "Segoe UI, sans-serif",
    background: "#f0f2f5",
    minHeight: "100vh",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
  },
  title: {
    margin: 0,
    fontSize: "24px",
    color: "#1a237e",
  },
  subtitle: {
    margin: "4px 0 0",
    color: "#666",
    fontSize: "14px",
  },
  logout: {
    padding: "8px 18px",
    background: "#d32f2f",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
  },
  cards: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "16px",
    marginBottom: "24px",
  },
  card: {
    background: "#fff",
    borderRadius: "10px",
    padding: "16px 20px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
    borderTop: "4px solid #1a237e",
  },
  cardLabel: {
    margin: 0,
    fontSize: "13px",
    color: "#888",
  },
  cardValue: {
    margin: "6px 0 0",
    fontSize: "26px",
    fontWeight: "700",
    color: "#1a237e",
  },
  filters: {
    display: "flex",
    gap: "12px",
    marginBottom: "16px",
    flexWrap: "wrap",
  },
  search: {
    flex: 1,
    minWidth: "220px",
    padding: "9px 14px",
    borderRadius: "6px",
    border: "1px solid #ccc",
    fontSize: "14px",
  },
  select: {
    padding: "9px 14px",
    borderRadius: "6px",
    border: "1px solid #ccc",
    fontSize: "14px",
    background: "#fff",
    cursor: "pointer",
  },
  tableWrapper: {
    overflowX: "auto",
    background: "#fff",
    borderRadius: "10px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "14px",
  },
  thead: {
    background: "#1a237e",
  },
  th: {
    padding: "12px 14px",
    color: "#fff",
    textAlign: "left",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  td: {
    padding: "10px 14px",
    borderBottom: "1px solid #eee",
    verticalAlign: "middle",
  },
  trEven: { background: "#fff" },
  trOdd:  { background: "#fafafa" },
  badge: {
    padding: "3px 10px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: 600,
    display: "inline-block",
  },
  msg: {
    textAlign: "center",
    padding: "40px",
    color: "#888",
    fontSize: "16px",
  },
};
